import { BackendDTO } from "@dto/backendResponse.dto";
import { MailGroup } from "@dto/mailGroup.dto";
import { MenuFavorites } from "@dto/menuFavorites.dto";
import { GraphInterval } from "@dto/pageResponse.dto";
import { PresetStatusColorType } from "antd/es/_util/colors";
import { createRef, Key, RefObject } from "react";
import { MenuNode } from "src/model/MenuNode";
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

const KEY_LEFT_TREE_WIDTH = "xm-left-tree-width";
const DEFAULT_TREE_WIDTH = 200;
const storedLeftTreeWidth = () => Number(localStorage.getItem(KEY_LEFT_TREE_WIDTH)) || DEFAULT_TREE_WIDTH;

const isLeftTreeOpenOnInitialLoad = () => {
  if (typeof window !== "undefined") {
    return window.innerWidth >= 800;
  }
  return true;
};

export type CustomGraphInterval = 'custom' | GraphInterval;

export class SyncBoxCtx {
  interval: CustomGraphInterval = 'day';
  showLegend: boolean = false;
  start: number;
  end: number;
  zoomStart?: number;
  zoomEnd?: number;
}

/** What XmGraphControls sets, as opposed to the zoom the graphs add on top. */
export type SyncBoxSlice = Pick<SyncBoxCtx, 'interval' | 'showLegend' | 'start' | 'end'>;

interface AppGlobalStore {

  changeLogModalTimestamp: number;
  emailGroups: MailGroup[];
  hostcfgStatus: PresetStatusColorType;
  info: BackendDTO;
  isCgHeatDash: boolean;
  isLeftTreeOpen: boolean;
  isMobile: boolean;
  isMobileLeftTreeOpen: boolean;
  leftMenu: MenuNode;
  leftMenuSelectedKeys: Key[];
  leftMenuExpandedKeys: Key[];
  leftTreeWidth: number;
  licenseStatus: PresetStatusColorType;
  mainScrollRef: RefObject<HTMLDivElement | null>;
  menuFavorites: MenuFavorites[];
  showMenu: boolean;
  syncBoxCtx: SyncBoxCtx;
  topMenu: MenuNode[];
  topMenuSelectedKeys: string[];
  topologyMenu: MenuNode[];
  trendsMenu: MenuNode[];

  // Actions
  addMenuFavorite: (favorite: MenuFavorites) => void;
  removeMenuFavorite: (url: string) => void;
  addEmailGroup: (group: MailGroup) => void;
  removeEmailGroup: (id: number) => void;
  updateEmailGroup: (group: MailGroup) => void;
  openDesktopLeftTree: () => void;
  closeDesktopLeftTree: () => void;
  updateWelcomeMsg: (msg: BackendDTO['backend']['welcome_message']) => void;
}

export const useAppGlobStore = create<AppGlobalStore>()(subscribeWithSelector((set) => ({
  mainScrollRef: createRef(),
  changeLogModalTimestamp: 0,

  topMenuSelectedKeys: [],
  topMenu: [],
  leftMenu: null,
  leftMenuSelectedKeys: [],
  leftMenuExpandedKeys: [],
  info: {} as BackendDTO,
  emailGroups: [],
  isCgHeatDash: false,
  showMenu: true,
  licenseStatus: 'default',
  hostcfgStatus: 'default',

  syncBoxCtx: new SyncBoxCtx(),

  isMobile: false,

  // LeftTree
  isLeftTreeOpen: isLeftTreeOpenOnInitialLoad(),
  leftTreeWidth: isLeftTreeOpenOnInitialLoad() ? storedLeftTreeWidth() : 0.5,
  isMobileLeftTreeOpen: false,

  menuFavorites: [],
  topologyMenu: [],
  trendsMenu: [],

  addMenuFavorite: (favorite: MenuFavorites) =>
    set((state) => ({ menuFavorites: [...state.menuFavorites, favorite].sort((a, b) => a.label.localeCompare(b.label)) })),
  removeMenuFavorite: (url: string) =>
    set((state) => ({
      menuFavorites: state.menuFavorites.filter((mf) => mf.url !== url),
    })),

  addEmailGroup: (group: MailGroup) =>
    set((state) => ({ emailGroups: [...state.emailGroups, group].sort(MailGroup.sorter) })),
  removeEmailGroup: (id: number) =>
    set((state) => ({ emailGroups: state.emailGroups.filter((mg) => mg.mail_group_id !== id) })),
  updateEmailGroup: (group: MailGroup) =>
    set((state) => ({
      emailGroups: state.emailGroups.map((mg) =>
        mg.mail_group_id === group.mail_group_id ? group : mg
      ).sort(MailGroup.sorter),
    })),
  updateWelcomeMsg: (msg) => {
    set(state => ({
      info: {
        ...state.info,
        backend: {
          ...state.info.backend,
          welcome_message: {
            ...state.info.backend.welcome_message,
            ...msg
          }
        }
      }
    }));
  },

  // LeftTree
  openDesktopLeftTree: () => {
    set(state => ({ isLeftTreeOpen: true, leftTreeWidth: state.isMobile ? window.innerWidth : storedLeftTreeWidth(), isMobileLeftTreeOpen: false }));
  },
  closeDesktopLeftTree: () => {
    set(state => ({ isLeftTreeOpen: false, leftTreeWidth: 1 }));
  }
} satisfies AppGlobalStore)));